<div class="dashboard-container">
  <!-- Header Section -->
  <div class="dashboard-header">
    <div class="dashboard-header__title">
      <h1 class="dashboard-title">Search prices</h1>
      <app-enrichment-mode-switch pageMode="price" />
    </div>
    <!-- Page-level actions. Channel scope, Import and Export act on the
         PAGE (which channel the whole list is read against; workbooks in and
         out), not on the query, so they sit with the title rather than in the
         toolbar that shapes the query. Import/Export keep their permission
         gates; Export stays selection-gated with its disabled-reason tooltip
         (it has no "export everything" mode — see canExportSelection). -->
    <div class="dashboard-header__actions">
      @if (isApplicationOwnerChannel) {
      <div class="channel-switcher">
        <button
          type="button"
          class="channel-pill"
          cdkOverlayOrigin
          #channelOrigin="cdkOverlayOrigin"
          [class.on]="channelPanelOpen"
          [class.empty]="!isChannelSelected"
          (click)="toggleChannelPanel($event)"
          aria-haspopup="listbox"
          [attr.aria-expanded]="channelPanelOpen"
        >
          <span class="dot" aria-hidden="true"></span>
          <span class="channel-pill-meta">Channel</span>
          <span class="channel-pill-label">
            @if (isChannelSelected) { {{ currentChannelLabel }} } @else {
            <span class="channel-pill-placeholder">Select channel</span>
            }
          </span>
          <svg width="10" height="10" viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="M4 6l4 4 4-4"
              stroke="currentColor"
              stroke-width="1.6"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>

        <!-- Portaled to the body (cdkConnectedOverlay) so the popover is not
             clipped by the .dashboard-card overflow:hidden when the table is
             short. Open/close + outside-click still driven by channelPanelOpen. -->
        <ng-template
          cdkConnectedOverlay
          [cdkConnectedOverlayOrigin]="channelOrigin"
          [cdkConnectedOverlayOpen]="channelPanelOpen"
          [cdkConnectedOverlayPositions]="channelPopoverPositions"
          [cdkConnectedOverlayPush]="true"
        >
          <!-- Defensive: keep the panel's own clicks from reaching the
               backdrop/host close handler. Not an interactive control. -->
          <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
          <div class="channel-popover" (click)="$event.stopPropagation()">
            <div class="channel-popover-head">
              <span class="channel-popover-title">Switch channel</span>
              @if (isChannelSelected) {
              <button
                type="button"
                class="channel-clear-btn"
                (click)="clearChannel($event)"
              >
                Clear
              </button>
              } @else {
              <span class="channel-popover-hint">All API calls use this</span>
              }
            </div>
            <div class="channel-search">
              <svg
                width="13"
                height="13"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="7"
                  cy="7"
                  r="4.5"
                  stroke="currentColor"
                  stroke-width="1.4"
                />
                <path
                  d="M11 11l3 3"
                  stroke="currentColor"
                  stroke-width="1.4"
                  stroke-linecap="round"
                />
              </svg>
              <input
                type="text"
                placeholder="Search channels…"
                [(ngModel)]="channelSearch"
                aria-label="Search channels"
              />
            </div>
            <div class="channel-list" role="listbox">
              @for (g of groupedChannels; track g.group) {
              <div class="channel-group-label">{{ g.group }}</div>
              @for (c of g.items; track c.value) {
              <button
                type="button"
                role="option"
                class="channel-option"
                [class.selected]="c.value === selectedChannel"
                [attr.aria-selected]="c.value === selectedChannel"
                (click)="selectChannel(c.value)"
              >
                <span class="channel-option-label">{{ c.label }}</span>
                <span class="channel-option-code">{{ c.value }}</span>
                @if (c.value === selectedChannel) {
                <span class="channel-option-check" aria-hidden="true">✓</span>
                }
              </button>
              } } @if (groupedChannels.length === 0) {
              <div class="channel-empty">
                No channels match "{{ channelSearch }}"
              </div>
              }
            </div>
          </div>
        </ng-template>
      </div>
      }
      <enr-button
        *hasPermission="Permissions.EnrichmentImport"
        data-testid="enrichment-import-button"
        variant="text"
        size="small"
        [leadingIcon]="true"
        [loading]="packageImportStatus() !== null"
        [disabled]="packageImportStatus() !== null"
        (clicked)="onImportClick()"
      >
        <span slot="leading-icon" class="flex items-center justify-center">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        </span>
        Import
      </enr-button>
      @if (packageImportStatus(); as status) {
      <span
        class="package-import-status"
        data-testid="package-import-status"
        role="status"
        >{{ status }}</span
      >
      }
      <!-- enrTooltip (the repo's standard tooltip mechanism — same
           directive/usage as history-log.html's field chips) drives the
           disabled-reason message via real mouseenter/focus listeners.
           NOT applied to <enr-button> itself: that component's HOST
           renders `display: contents` (confirmed in the compiled
           omniark-ui source — `[_nghost-%COMP%]{display:contents}` on
           ButtonComponent), so it generates no box of its own and
           `getBoundingClientRect()` on it is all zeros — CDK's connected
           position strategy then anchors the tooltip at (0,0), i.e. the
           page's top-left corner (confirmed live). Wrapping in a plain
           `<span>` — a real, boxed element, exactly like history-log's
           field-chip span — gives the directive a real anchor to connect
           to; `display: contents` on the button doesn't break the span's
           own layout since the button's children still flow inside it. -->
      <span
        *hasPermission="Permissions.EnrichmentExport"
        class="export-tooltip-anchor"
        [enrTooltip]="exportButtonTitle"
        enrTooltipPosition="top"
      >
        <enr-button
          data-testid="enrichment-export-button"
          variant="text"
          size="small"
          [leadingIcon]="true"
          [disabled]="!canExportSelection"
          (clicked)="onExportClick()"
        >
          <span slot="leading-icon" class="flex items-center justify-center">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </span>
          {{ exportButtonLabel }}
        </enr-button>
      </span>
      <input
        #enrichmentImportFileInput
        type="file"
        accept=".xlsx"
        hidden
        (change)="onImportFileSelected($event)"
      />
    </div>
  </div>

  <!-- Content Card -->
  <div class="dashboard-card">
    <!-- Not <enr-tab>: it gives a disabled tab `pointer-events: none`, so the
         "pick a channel first" tooltip could never open on it. -->
    <div class="view-tabs">
      <div class="enrichment-filter" role="group" aria-label="Price status">
        <div class="enrichment-filter__segments">
          @for (tab of priceStatusTabs; track tab.value) {
          <button
            type="button"
            class="enrichment-filter__segment"
            [class.on]="priceStatusTab === tab.value"
            [class.is-disabled]="isPriceStatusTabDisabled"
            [attr.aria-disabled]="isPriceStatusTabDisabled"
            [attr.aria-pressed]="priceStatusTab === tab.value"
            [attr.data-testid]="'enrichment-status-tab-' + tab.value"
            [enrTooltip]="tabTitle(tab)"
            enrTooltipPosition="top"
            (click)="selectPriceStatusTab(tab.value)"
          >
            <span class="enrichment-filter__segment-label"
              >{{ tab.label }}</span
            >
            @if (priceStatusTab === tab.value && shownHere?.tab === tab.value) {
            <span
              class="inline-grid h-5 min-w-5.5 place-items-center rounded-full bg-[#f1f8e3] px-1.75 text-[11.5px] font-bold text-[#4a6b14] tabular-nums"
              aria-hidden="true"
              data-testid="enrichment-list-total"
              >{{ totalCount }}</span
            >
            <span class="sr-only">{{ totalLabel }}</span>
            }
          </button>
          }
        </div>
      </div>
      @if (canCountPackages) {
      <div
        class="ml-auto flex shrink-0 items-center gap-2 py-2 text-xs text-gray-500"
        data-testid="enrichment-list-package-count"
      >
        @switch (packageCount.kind) { @case ("idle") {
        <button
          type="button"
          class="text-xs text-gray-500 hover:text-gray-900 hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
          data-testid="enrichment-list-count-packages"
          [disabled]="loading"
          (click)="countPackages()"
        >
          Count product offerings
        </button>
        } @case ("loading") {
        <span>Counting…</span>
        } @case ("ready") {
        <span
          ><span class="font-semibold tabular-nums text-gray-900"
            >{{ totalCount }}</span
          >
          {{ totalCount === "1" ? "price" : "prices" }} ·
          <span
            [enrTooltip]="packageCountTooltip"
            enrTooltipPosition="top"
            data-testid="enrichment-list-package-total"
            ><span class="font-semibold tabular-nums text-gray-900"
              >{{ packageTotal }}</span
            >
            {{ packageTotal === "1" ? "product offering" : "product offerings"
            }}</span
          ></span
        >
        } @case ("failed") {
        <span>Couldn't count product offerings</span>
        <button
          type="button"
          class="text-xs font-semibold text-gray-700 hover:text-gray-900 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="enrichment-list-count-packages-retry"
          [disabled]="loading"
          (click)="countPackages()"
        >
          Retry
        </button>
        } }
      </div>
      }
    </div>

    <!-- Toolbar Section -->
    <div class="toolbar-section">
      <!-- Left: Search (grows to fill) -->
      <div class="toolbar-left">
        <!-- (paste) lives on the wrapper: the native input is inside
             <enr-search>, and the event bubbles up here. Only a code scope
             takes it over (see onSearchPaste). -->
        <div class="search-container" (paste)="onSearchPaste($event)">
          <!-- Search scope. `q` (free text) and the exact-match code filters
               are mutually exclusive on the wire, so they share ONE box with a
               scope in front of it rather than sitting in competing inputs:
               the exclusion becomes a visible property of the control instead
               of a silent clear, and the toolbar gains no extra width. The
               same three code fields remain editable in the filter drawer's
               "Search by code" section — this is a second front door, not a
               second mechanism. -->
          <div class="search-scope">
            <button
              type="button"
              class="search-scope__pill"
              cdkOverlayOrigin
              #scopeOrigin="cdkOverlayOrigin"
              [class.on]="scopePanelOpen"
              [class.is-code]="searchScope !== 'all'"
              data-testid="enrichment-search-scope"
              (click)="toggleScopePanel($event)"
              aria-haspopup="listbox"
              [attr.aria-expanded]="scopePanelOpen"
              [attr.aria-label]="'Search scope: ' + searchScopeLabel"
            >
              <span class="search-scope__label">{{ searchScopeLabel }}</span>
              <svg
                width="10"
                height="10"
                viewBox="0 0 16 16"
                aria-hidden="true"
              >
                <path
                  d="M4 6l4 4 4-4"
                  stroke="currentColor"
                  stroke-width="1.6"
                  fill="none"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>

            <!-- Portaled to the body for the same reason as the channel
                 popover: .dashboard-card is overflow:hidden and would clip it
                 whenever the table is short. -->
            <ng-template
              cdkConnectedOverlay
              [cdkConnectedOverlayOrigin]="scopeOrigin"
              [cdkConnectedOverlayOpen]="scopePanelOpen"
              [cdkConnectedOverlayPositions]="channelPopoverPositions"
              [cdkConnectedOverlayPush]="true"
            >
              <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
              <div
                class="search-scope__popover"
                (click)="$event.stopPropagation()"
              >
                <div class="search-scope__popover-head">Search in</div>
                <div role="listbox" aria-label="Search scope">
                  @for (opt of searchScopeOptions; track opt.value) {
                  <button
                    type="button"
                    role="option"
                    class="search-scope__option"
                    [class.on]="!isMultiCodeScope && searchScope === opt.value"
                    [attr.aria-selected]="
                      !isMultiCodeScope && searchScope === opt.value
                    "
                    (click)="selectSearchScope(opt.value, $event)"
                  >
                    <span class="search-scope__option-label"
                      >{{ opt.label }}</span
                    >
                    <span class="search-scope__option-hint"
                      >{{ opt.detail }}</span
                    >
                  </button>
                  }
                </div>
                <!-- The one affordance that reaches the read-only multi-code
                     state, where <enr-search> hides its own × because the box
                     is disabled. -->
                @if (committedCodeScopes.length) {
                <button
                  type="button"
                  class="search-scope__clear"
                  data-testid="enrichment-search-scope-clear"
                  (click)="clearCodeScope(); scopePanelOpen = false"
                >
                  Clear code search
                </button>
                }
              </div>
            </ng-template>
          </div>

          <enr-search
            #searchInput
            [placeholder]="searchBoxPlaceholder"
            [value]="searchBoxValue"
            [clearable]="true"
            size="medium"
            [disabled]="searchBoxDisabled"
            (search)="onSearch($event)"
            (clear)="onSearchClear()"
            (keyup.enter)="onSearchEnter()"
          />

          <!-- The hint line. Rendered here rather than through
               `<enr-search supportingText>` (a plain-string input) so the
               prefix legend can be chips: "which one is the product code?" is
               answered by shape, not by a sentence the eye has to parse. It
               is the LAST child of .search-container, which is flex-wrap, so
               `flex: 1 1 100%` puts it on its own line under the field. -->
          <!-- Committed codes as chips (the strip the drawer textarea never
               had): what was counted, what was dropped, and a × per code.
               Takes the hint line's slot — once codes are committed, the
               format guidance has done its job. Several groups appear only
               after a drawer apply set more than one code field; the group
               label is then the only thing telling them apart. -->
          @if (committedCodeGroups.length) {
          <div
            class="search-tokens"
            data-testid="enrichment-search-tokens"
            aria-label="Codes in this search"
          >
            @for (group of committedCodeGroups; track group.scope) {
            <span class="search-tokens__group">
              @if (committedCodeGroups.length > 1) {
              <span class="search-tokens__group-label">{{ group.label }}</span>
              } @for (token of group.tokens; track token.value) {
              <span
                class="search-tokens__chip"
                [class.is-ignored]="token.ignored"
                [attr.title]="
                  token.ignored
                    ? 'Not searched — a product code starts with O, a product number with P'
                    : null
                "
              >
                <code>{{ token.value }}</code>
                @if (token.ignored) {
                <span class="search-tokens__ignored">ignored</span>
                }
                <button
                  type="button"
                  class="search-tokens__remove"
                  [attr.aria-label]="'Remove ' + token.value"
                  (click)="removeCodeToken(group.scope, token.value)"
                >
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 16 16"
                    aria-hidden="true"
                  >
                    <path
                      d="M4 4l8 8M12 4l-8 8"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                    />
                  </svg>
                </button>
              </span>
              }
            </span>
            }
            <span class="search-tokens__count">
              {{ committedCodeCount }} {{ committedCodeCount === 1 ? 'code' :
              'codes' }}
            </span>
            <button
              type="button"
              class="search-tokens__clear"
              data-testid="enrichment-search-tokens-clear"
              (click)="clearCodeScope()"
            >
              Clear all
            </button>
          </div>
          } @else if (searchHint.kind !== 'none') {
          <p
            class="search-hint"
            [class.search-hint--warn]="searchHint.kind === 'warn'"
            data-testid="enrichment-search-hint"
            [attr.role]="
              searchHint.kind === 'warn'
                ? 'alert'
                : searchHint.kind === 'notice'
                  ? 'status'
                  : null
            "
          >
            @if (searchHint.kind === 'warn') {
            <svg
              class="search-hint__glyph"
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M12 4.5 2.8 20h18.4L12 4.5Z"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linejoin="round"
              />
              <path
                d="M12 10.5v4"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
              <circle cx="12" cy="17.4" r="1" fill="currentColor" />
            </svg>
            <span>{{ searchHint.text }}</span>
            } @else if (searchHint.kind === 'notice') {
            <span>{{ searchHint.text }}</span>
            } @else { @if (activeScopeOption.hint) {
            <span>{{ activeScopeOption.hint }}</span>
            } @for (chip of activeScopeOption.chips; track chip.key) {
            <span class="search-hint__chip">
              <code>{{ chip.key }}</code>
              {{ chip.text }}
            </span>
            }
            <span class="search-hint__enter">
              <kbd>Enter</kbd>
              to search
            </span>
            }
          </p>
          }
          <!-- The ⓘ explains what the free-text `q` matches; in a code scope
               that description is simply wrong, and the scope's own
               supportingText carries the format hint instead. -->
          @if (searchScope === 'all') {
          <span class="search-info">
            <button
              type="button"
              class="search-info__btn"
              aria-label="Search by price or product offering name, ID, or description. Product codes have their own Product code scope. ค้นหาจากชื่อ รหัส หรือคำอธิบายของราคาหรือแพ็กเกจ ค้นหา product code ได้ในขอบเขต Product code"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  stroke="currentColor"
                  stroke-width="1.8"
                />
                <path
                  d="M12 11v5"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />
                <circle cx="12" cy="7.75" r="1.15" fill="currentColor" />
              </svg>
            </button>
            <span class="search-info__tip" role="tooltip">
              <span class="search-info__tip-en"
                >Search by price or product offering name, ID, or description.
                Product codes have their own Product code scope.</span
              >
              <span class="search-info__tip-th"
                >ค้นหาจากชื่อ รหัส หรือคำอธิบายของราคาหรือแพ็กเกจ ค้นหา product
                code ได้ในขอบเขต Product code</span
              >
            </span>
          </span>
          }
        </div>
      </div>

      <!-- Right: Sort · Filter (icon) · Columns (icon). Only the controls
           that shape or present the QUERY live here — page actions moved to
           the header, the enrichment axis to the view tabs above. -->
      <div class="toolbar-right">
        <div class="sort-select-wrap">
          <enr-select
            id="enr-enrichment-list-sort"
            placeholder="Sort"
            [options]="sortOptions"
            [formControl]="sortControl"
          ></enr-select>
        </div>

        <!-- Filter (icon-only) -->
        <button
          type="button"
          class="toolbar-icon-btn"
          [class.active]="drawerFilterCount > 0"
          (click)="onFilter()"
          aria-label="Filters"
          title="Filters"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          @if (activeFilterCount > 0) {
          <span
            class="toolbar-icon-btn__badge"
            [attr.data-count]="activeFilterCount"
          ></span>
          }
        </button>

        <!-- Column Toggle Button (icon-only, online-store style) -->
        <div class="column-toggle-wrap">
          <button
            type="button"
            class="toolbar-icon-btn"
            cdkOverlayOrigin
            #columnsOrigin="cdkOverlayOrigin"
            [class.active]="columnsPanelOpen"
            (click)="toggleColumnsPanel($event)"
            aria-label="Toggle columns"
            title="Show / hide columns"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="3" y="3" width="7" height="7" />
              <rect x="14" y="3" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" />
            </svg>
          </button>

          <!-- Portaled to the body (cdkConnectedOverlay) so the panel isn't
               clipped by the .dashboard-card overflow:hidden. -->
          <ng-template
            cdkConnectedOverlay
            [cdkConnectedOverlayOrigin]="columnsOrigin"
            [cdkConnectedOverlayOpen]="columnsPanelOpen"
            [cdkConnectedOverlayPositions]="columnsPanelPositions"
            [cdkConnectedOverlayPush]="true"
          >
            <!-- Defensive: keep the panel's own clicks from reaching the
                 backdrop/host close handler. Not an interactive control. -->
            <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
            <div
              class="column-filter-dropdown"
              (click)="$event.stopPropagation()"
            >
              <!-- Locked columns (always-on) — render outside drop list -->
              @for (col of allColumns; track col.key) { @if
              (isColumnLocked(col.key)) {
              <div class="column-filter-item locked">
                <enr-checkbox
                  [name]="col.key"
                  [checked]="isColumnVisible(col.key)"
                  [disabled]="true"
                />
                <span class="column-filter-item__label">{{ col.label }}</span>
                <span class="column-filter-item__locked">required</span>
              </div>
              } }

              <!-- Reorderable columns — drag-drop list, rendered in the current
                 order (visible first, then hidden) so reorders are visible and
                 stack across drags. -->
              <div
                class="column-filter-list"
                cdkDropList
                (cdkDropListDropped)="onColumnDrop($event)"
              >
                @for (col of reorderableColumns; track col.key) {
                <div class="column-filter-item" cdkDrag>
                  <enr-checkbox
                    [name]="col.key"
                    [checked]="isColumnVisible(col.key)"
                    (change)="toggleColumn(col.key)"
                  />
                  <span class="column-filter-item__label">{{ col.label }}</span>
                  <span class="column-filter-item__drag" cdkDragHandle>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <circle cx="9" cy="6" r="1.5" />
                      <circle cx="15" cy="6" r="1.5" />
                      <circle cx="9" cy="12" r="1.5" />
                      <circle cx="15" cy="12" r="1.5" />
                      <circle cx="9" cy="18" r="1.5" />
                      <circle cx="15" cy="18" r="1.5" />
                    </svg>
                  </span>
                </div>
                }
              </div>

              <div class="column-filter-actions">
                <enr-button
                  variant="outline"
                  size="small"
                  (clicked)="resetColumns()"
                >
                  Reset
                </enr-button>
                <enr-button
                  variant="primary"
                  size="small"
                  (clicked)="applyColumnFilter()"
                >
                  Apply
                </enr-button>
              </div>
            </div>
          </ng-template>
        </div>
      </div>
    </div>

    <!-- Active filter chips -->
    @if (hasAnyFilter) {
    <div class="active-filters">
      <span class="active-filters__label">Filtered by</span>
      @if (searchTerm) {
      <span class="filter-chip">
        <span class="filter-chip__key">query:</span>"{{ searchTerm }}"
        <button
          type="button"
          class="filter-chip__close"
          (click)="onClearSearch()"
          aria-label="Clear search"
        >
          ×
        </button>
      </span>
      } @if (drawerFilterCount > 0) {
      <span class="filter-chip">
        <span class="filter-chip__key">filters:</span>{{ drawerFilterCount }}
        <button
          type="button"
          class="filter-chip__close"
          (click)="onFilterCleared()"
          aria-label="Clear drawer filters"
        >
          ×
        </button>
      </span>
      }
      <button type="button" class="clear-all-btn" (click)="onClearAllFilters()">
        Clear all
      </button>
    </div>
    } @if (listScope === "masters") {
    <div class="no-channel-bar" role="note">
      <span class="no-channel-bar__badge" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path
            d="M3 7.5 12 3l9 4.5-9 4.5-9-4.5Z"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linejoin="round"
          />
          <path
            d="M7.5 10.2v4.3c0 1.2 2 2.5 4.5 2.5s4.5-1.3 4.5-2.5v-4.3"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
      <div class="no-channel-bar__text">
        <span class="no-channel-bar__en">
          <strong>Master prices</strong> — prices of parent offerings, not
          scoped to a channel. Pick a channel to see each price's status there.
        </span>
        <span class="no-channel-bar__th">
          ราคาของแพ็กเกจระดับ master (ยังไม่ผูก channel) — เลือก channel
          เพื่อดูสถานะของแต่ละราคาใน channel นั้น
        </span>
      </div>
      <button
        type="button"
        class="no-channel-bar__btn"
        (click)="toggleChannelPanel($event)"
      >
        <span>Select channel</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M9 6l6 6-6 6"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
    } @else if (listScope === "none") {
    <div
      class="no-channel-bar"
      role="note"
      data-testid="enrichment-list-no-channel-role"
    >
      <span class="no-channel-bar__badge" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle
            cx="12"
            cy="12"
            r="9"
            stroke="currentColor"
            stroke-width="1.7"
          />
          <path
            d="M12 11v5"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />
          <circle cx="12" cy="8" r="1.05" fill="currentColor" />
        </svg>
      </span>
      <div class="no-channel-bar__text">
        <span class="no-channel-bar__en">
          <strong>No channel for this role</strong> — prices are listed per
          channel. Switch to a role with a channel, or ask an admin to add one.
        </span>
        <span class="no-channel-bar__th">
          บทบาทนี้ยังไม่มี channel — ราคาแสดงตาม channel
          กรุณาเปลี่ยนไปใช้บทบาทที่มี channel หรือติดต่อผู้ดูแลระบบให้เพิ่ม
          channel
        </span>
      </div>
    </div>
    }

    <!-- Deleted view notice — under the "Deleted packages" filter every row
         is a soft-deleted price, whatever its product offering's own status.
         Same structure as the no-channel bar, rose palette; the button
         un-ticks the filter through the drawer's Apply path. -->
    @if (isDeletedView) {
    <div
      class="deleted-view-bar"
      role="note"
      data-testid="enrichment-deleted-view-bar"
    >
      <span class="deleted-view-bar__badge" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path
            d="M4 7h16"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />
          <path
            d="M9.5 7V5.5A1.5 1.5 0 0 1 11 4h2a1.5 1.5 0 0 1 1.5 1.5V7"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linejoin="round"
          />
          <rect
            x="6"
            y="7"
            width="12"
            height="13"
            rx="1.6"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linejoin="round"
          />
          <path
            d="M10 11v5M14 11v5"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />
        </svg>
      </span>
      <div class="deleted-view-bar__text">
        <span class="deleted-view-bar__en">
          <strong>Deleted packages</strong> — every row here is a price that has
          been deleted. Its product offering may still be active.
        </span>
        <span class="deleted-view-bar__th">
          แพ็กเกจที่ลบแล้ว — ทุกแถวคือราคาที่ถูกลบ product offering
          ของราคานั้นอาจยังใช้งานอยู่
        </span>
      </div>
      <button
        type="button"
        class="deleted-view-bar__btn"
        data-testid="enrichment-deleted-view-clear"
        (click)="clearDeletedView()"
      >
        Show active packages
      </button>
    </div>
    } @if (searchTruncated) {
    <div class="search-truncated-bar" role="status" aria-live="polite">
      <span class="search-truncated-bar__badge" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path
            d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linejoin="round"
          />
          <path
            d="M12 9v4"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />
          <circle cx="12" cy="17" r="1.05" fill="currentColor" />
        </svg>
      </span>
      <div class="search-truncated-bar__text">
        @if (shown?.tab === "all") {
        <span class="search-truncated-bar__en">
          <strong>Too many matches</strong> — some results aren't shown. Refine
          your search with a more specific term to see them all.
        </span>
        <span class="search-truncated-bar__th">
          ผลการค้นหามีจำนวนมากเกินไป แสดงผลไม่ครบทั้งหมด
          กรุณาระบุคำค้นให้เฉพาะเจาะจงมากขึ้น
        </span>
        } @else {
        <span
          class="search-truncated-bar__en"
          data-testid="enrichment-list-status-cap"
        >
          <strong
            >More than {{ statusLookupCap }} {{ shownStatusTab.label }} prices
            on {{ currentChannelLabel }}.</strong
          >
          This tab shows only some of them. To find a specific price, search for
          it on the All tab.
        </span>
        <span class="search-truncated-bar__th">
          มีราคาสถานะ {{ shownStatusTab.label }} บน {{ currentChannelLabel }}
          มากกว่า {{ statusLookupCap }} รายการ แท็บนี้แสดงได้เพียงบางส่วน
          หากต้องการหาราคาใดราคาหนึ่ง ให้ค้นหาในแท็บ All
        </span>
        }
      </div>
    </div>
    } @if (allStatusesOffer) {
    <div
      class="no-channel-bar"
      role="status"
      aria-live="polite"
      data-testid="enrichment-list-all-statuses-offer"
    >
      <span class="no-channel-bar__badge" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle
            cx="11"
            cy="11"
            r="7"
            stroke="currentColor"
            stroke-width="1.7"
          />
          <path
            d="m20 20-3.5-3.5"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />
        </svg>
      </span>
      <div class="no-channel-bar__text">
        <span class="no-channel-bar__en">
          No {{ activeStatusTab.label }} prices match these codes. They may have
          another status.
        </span>
        <span class="no-channel-bar__th">
          ไม่มีราคาสถานะ {{ activeStatusTab.label }} ที่ตรงกับรหัสเหล่านี้
          ราคาอาจมีสถานะอื่น
        </span>
      </div>
      <button
        type="button"
        class="no-channel-bar__btn"
        data-testid="enrichment-list-all-statuses-btn"
        (click)="selectPriceStatusTab('all')"
      >
        Search all statuses
      </button>
    </div>
    } @if (codeSearchOffer) {
    <div
      class="no-channel-bar"
      role="status"
      aria-live="polite"
      data-testid="enrichment-list-code-offer"
    >
      <span class="no-channel-bar__badge" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle
            cx="11"
            cy="11"
            r="7"
            stroke="currentColor"
            stroke-width="1.7"
          />
          <path
            d="m20 20-3.5-3.5"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />
        </svg>
      </span>
      <div class="no-channel-bar__text">
        <span class="no-channel-bar__en">
          No price or product offering name, ID or description contains "{{
          codeSearchOffer }}". Looks like a product code.
        </span>
        <span class="no-channel-bar__th">
          ไม่มีชื่อ รหัส หรือคำอธิบายของราคาหรือแพ็กเกจที่ตรงกับ "{{
          codeSearchOffer }}" — ดูเหมือนเป็น product code
        </span>
      </div>
      <button
        type="button"
        class="no-channel-bar__btn"
        data-testid="enrichment-list-code-offer-btn"
        (click)="searchAsProductCode()"
      >
        Search Product code
      </button>
    </div>
    }

    <!-- Bulk selection action bar — a floating pill at the bottom of the
         viewport once one or more rows are checked. "Assign shelves" adds
         catalog shelves to every selected price (add-only). -->
    @if (hasSelection) {
    <div class="selection-bar" role="region" aria-label="Bulk actions">
      <button
        type="button"
        class="selection-bar__close"
        (click)="clearSelection()"
        aria-label="Clear selection"
      >
        ✕
      </button>
      <span class="selection-bar__count">
        <span class="selection-bar__badge">{{ selectedPriceCount }}</span>
        <span data-testid="enrichment-selection-count"
          >{{ selectedPriceCount === 1 ? "price" : "prices" }} from {{
          selectedPackageIds.length }} {{ selectedPackageIds.length === 1 ?
          "product offering" : "product offerings" }}</span
        >
      </span>

      <!-- The checkbox column can come from `enrichment:export` alone, so the
           assign action carries its own `enrichment:update` gate rather than
           riding on the shared selection surface. -->
      @if (canAssignCategories) { @if (assignNeedsChannel) {
      <span class="selection-bar__hint">
        Pick a channel scope first — "Assign shelves" will open the picker
      </span>
      }

      <enr-button
        variant="primary"
        size="small"
        [disabled]="transitionSending"
        data-testid="enrichment-bulk-assign"
        (clicked)="openCategoryAssign()"
      >
        Assign shelves
      </enr-button>
      } @for (item of bulkLifecycleActions; track item.action) {
      <span [enrTooltip]="item.tooltip" enrTooltipPosition="top">
        <enr-button
          variant="outline"
          size="small"
          [disabled]="item.disabled"
          [attr.data-testid]="'enrichment-bulk-' + item.menuId"
          (clicked)="openTransition(item.action, selectedRows)"
        >
          {{ item.label }} · {{ item.eligible }}
        </enr-button>
      </span>
      }
    </div>
    }

    <div class="table-section">
      <enr-table
        [data]="tableData"
        [columns]="orderedTableColumns"
        [visibleColumns]="visibleColumns"
        [actions]="tableActions()"
        [config]="tableConfig"
        [loading]="loading"
        [checked]="checkedRows"
        [trackByFn]="trackByRowKey"
        [totalItems]="config.totalItems"
        (actionClick)="onTableAction($event)"
        (selectionChange)="onSelectionChange($event)"
        (pageChange)="onPageChange($event)"
        (pageSizeChange)="onPageSizeChange($event)"
      >
        <!-- Labels may wrap onto a second line so the default columns fit
             without the table scrolling sideways. -->
        <ng-template #customHeader let-column="column">
          <span class="whitespace-normal">{{ column.label }}</span>
        </ng-template>
        <ng-template #customCell let-item let-column="column">
          @switch (column.key) { @case ("price") {
          <!-- The title opens the EDITOR; the row itself is not a link, so
               building a selection never navigates away. -->
          <div
            class="cell-with-desc max-w-34"
            [class.cell-recent]="item.lastUpdate.recent"
          >
            <span class="flex min-w-0 items-center gap-1">
              <app-price-offset-badge
                [offset]="item.raw.offset"
                [showLabel]="false"
              />
              <code
                class="truncate font-mono text-sm leading-tight font-semibold text-gray-900"
                [title]="item.priceId"
                [attr.data-testid]="'enrichment-list-price-id-' + item.key"
                >{{ item.priceId }}</code
              >
              <button
                type="button"
                class="cell-id-copy"
                [attr.aria-label]="'Copy price ID ' + item.priceId"
                title="Copy price ID"
                [attr.data-testid]="'enrichment-list-copy-price-' + item.key"
                (click)="copyPriceId(item.priceId); $event.stopPropagation()"
              >
                <span class="material-icons" aria-hidden="true"
                  >content_copy</span
                >
              </button>
            </span>
            <span
              class="cell-primary cell-primary--edit"
              role="button"
              tabindex="0"
              [attr.aria-label]="'Edit ' + item.title"
              [attr.data-testid]="'enrichment-list-edit-price-' + item.key"
              [enrTooltip]="item.title"
              enrTooltipPosition="bottom"
              (click)="handleEditAction(item)"
              (keydown.enter)="handleEditAction(item)"
              (keydown.space)="handleEditAction(item); $event.preventDefault()"
            >
              <span class="cell-primary__text">{{ item.title }}</span>
              <span
                class="material-icons cell-primary__glyph"
                aria-hidden="true"
                >edit</span
              >
            </span>
            @if (item.subtitle) {
            <span class="truncate text-xs text-gray-600" [title]="item.subtitle"
              >{{ item.subtitle }}</span
            >
            } @if (item.productCode || appLinkFor(item)) {
            <span class="flex flex-wrap items-center gap-1.5">
              @if (item.productCode) {
              <span
                class="rounded border border-gray-200 px-1 font-mono text-xs text-gray-700"
                [attr.data-testid]="'enrichment-list-product-code-' + item.key"
                >{{ item.productCode }}</span
              >
              } @if (appLinkFor(item); as link) {
              <!-- myAIS deep link for this price. Named rather than a bare
                   icon; the tooltip carries the whole URL. -->
              <button
                type="button"
                class="inline-flex items-center gap-0.5 rounded border border-gray-200 px-1 text-xs text-gray-700 hover:bg-gray-50"
                [attr.data-testid]="'enrichment-list-btn-app-link-' + item.key"
                [enrTooltip]="link"
                enrTooltipPosition="bottom"
                [enrTooltipShowDelay]="0"
                [attr.aria-label]="'Generate Deeplink for ' + item.title"
                (click)="copyAppLink(item); $event.stopPropagation()"
              >
                <span class="material-icons text-sm" aria-hidden="true"
                  >link</span
                >
                Deeplink
              </button>
              }
            </span>
            }
          </div>
          } @case ("package") {
          <div class="flex max-w-30 min-w-0 flex-col gap-0.5">
            <button
              type="button"
              class="truncate text-left text-sm text-gray-900 hover:underline"
              [attr.data-testid]="'enrichment-list-package-' + item.key"
              [enrTooltip]="'Show only this product offering’s prices'"
              enrTooltipPosition="bottom"
              (click)="narrowToPackage(item)"
            >
              {{ item.offeringName || item.offeringId }}
            </button>
            <span
              class="flex min-w-0 items-center gap-1.5 text-xs text-gray-500"
            >
              <span class="truncate font-mono" [title]="item.offeringId"
                >{{ item.offeringId }}</span
              >
            </span>
            <!-- Dot + text, never a pill: the coloured pill in Price status
                 is the price's own state, and two pills per row read as one
                 status. The dot and hue repeat the pill's vocabulary. -->
            @if (item.packageStatus; as pkg) {
            <span
              class="mt-0.5 inline-flex w-fit flex-wrap items-center gap-x-2 text-sm leading-5"
              [attr.data-testid]="'enrichment-list-package-status-' + item.key"
              [enrTooltip]="packageStatusTip(item, pkg)"
              enrTooltipPosition="bottom"
            >
              <!-- Launched is the only package state customers can buy, and
                   it shares its green family with Active. A check in place
                   of the dot keeps the two apart by shape, not hue alone. -->
              @if (pkg.tone === "sellable") {
              <span
                class="inline-flex shrink-0"
                [class]="packageTone(pkg).text"
                aria-hidden="true"
              >
                <svg
                  class="size-3.5 shrink-0"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                  data-testid="enrichment-list-package-sellable-icon"
                >
                  <circle cx="8" cy="8" r="7" fill="currentColor" />
                  <path
                    d="M5 8.2l2 2 4-4.4"
                    stroke="#fff"
                    stroke-width="1.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              } @else {
              <span
                class="inline-flex size-2 shrink-0 rounded-full"
                [class]="packageTone(pkg).dot"
                aria-hidden="true"
              ></span>
              }
              <span
                class="whitespace-nowrap font-semibold"
                [class]="packageTone(pkg).text"
                >{{ pkg.label }}</span
              >
              @if (pkg.tone === "sellable") {
              <span
                class="whitespace-nowrap font-medium"
                [class]="packageTone(pkg).text"
                >· sellable</span
              >
              } @else {
              <span class="whitespace-nowrap text-gray-500"
                >· offering status</span
              >
              }
            </span>
            }
          </div>
          } @case ("version") { @if (item.offeringVersion || item.priceVersion)
          {
          <div class="flex flex-col font-mono text-xs">
            <span
              class="text-gray-900"
              [attr.data-testid]="'enrichment-list-version-' + item.key"
              >v{{ item.offeringVersion || item.priceVersion }}</span
            >
            @if (item.offeringVersion && item.priceVersion && item.priceVersion
            !== item.offeringVersion) {
            <span
              class="whitespace-nowrap text-gray-500"
              [attr.data-testid]="'enrichment-list-price-version-' + item.key"
              >price v{{ item.priceVersion }}</span
            >
            }
          </div>
          } @else {
          <span
            class="cell-empty"
            [attr.data-testid]="'enrichment-list-version-' + item.key"
            >—</span
          >
          } } @case ("productLine") { @if (item.productLine) {
          <span
            class="block max-w-15 truncate text-sm"
            [title]="item.productLine"
            [attr.data-testid]="'enrichment-list-product-line-' + item.key"
            >{{ item.productLine }}</span
          >
          } @else {
          <span
            class="cell-empty"
            [attr.data-testid]="'enrichment-list-product-line-' + item.key"
            >—</span
          >
          } } @case ("productType") { @if (item.productType) {
          <span
            class="block max-w-15 truncate text-sm"
            [title]="item.productType"
            [attr.data-testid]="'enrichment-list-product-type-' + item.key"
            >{{ item.productType }}</span
          >
          } @else {
          <span
            class="cell-empty"
            [attr.data-testid]="'enrichment-list-product-type-' + item.key"
            >—</span
          >
          } } @case ("offerClassification") { @if (item.offerClassification) {
          <span
            class="block max-w-24 truncate text-sm"
            [title]="item.offerClassification"
            [attr.data-testid]="'enrichment-list-offer-classification-' + item.key"
            >{{ item.offerClassification }}</span
          >
          } @else {
          <span
            class="cell-empty"
            [attr.data-testid]="'enrichment-list-offer-classification-' + item.key"
            >—</span
          >
          } } @case ("amount") {
          <div class="flex max-w-16 flex-col items-end">
            <span
              class="max-w-full truncate font-semibold"
              [title]="item.amount"
              >{{ item.amount }}</span
            >
            @if (item.priceType) {
            <span
              class="max-w-full truncate text-xs text-gray-500"
              [title]="item.priceType"
              >{{ item.priceType }}</span
            >
            }
          </div>
          } @case ("status") {
          <div class="flex flex-col items-start gap-1">
            @if (isDeletedView && item.deleteDate) {
            <!-- The deletion supersedes the lifecycle pill. Gated on the view:
                 on the live list a deleteDate is a deletion still scheduled
                 for the future, and that price is not deleted yet. -->
            <span
              class="status-pill status-deleted"
              [attr.data-testid]="'enrichment-list-deleted-' + item.key"
              [title]="'Deleted on ' + item.deleteDate"
            >
              <span class="status-pill__dot"></span>
              Deleted
            </span>
            <span
              class="text-xs text-gray-500"
              [attr.data-testid]="'enrichment-list-deleted-on-' + item.key"
              >{{ item.deleteDate }}</span
            >
            } @else {
            <span
              [class]="'status-pill ' + statusClass(item)"
              [attr.data-testid]="'enrichment-list-status-' + item.key"
            >
              <span class="status-pill__dot"></span>
              {{ statusLabel(item) }}
            </span>
            <!-- A queued price reads as Designed or live like any other, so the
                 status alone cannot say a change is already booked. -->
            @if (scheduleChipFor(item); as when) {
            <span
              class="inline-flex items-center gap-1 text-xs text-gray-600"
              [attr.data-testid]="'enrichment-list-scheduled-' + item.key"
              [enrTooltip]="'Scheduled · ' + scheduleZoneLabel"
              enrTooltipPosition="bottom"
            >
              <span class="material-icons text-sm" aria-hidden="true"
                >schedule</span
              >
              {{ when }}
            </span>
            } }
          </div>
          } @case ("enriched") {
          <ng-container
            *ngTemplateOutlet="
              enrichedChannelPills;
              context: { $implicit: item.enrichedChannels, item: item }
            "
          />
          } @case ("shelves") { @if (item.shelves.length) {
          <div class="shelf-cell">
            @for (sh of item.shelves; track sh.id) {
            <span class="shelf-pill" [title]="sh.name">{{ sh.name }}</span>
            }
          </div>
          } @else {
          <span class="cell-empty">—</span>
          } } @case ("channels") {
          <ng-container
            *ngTemplateOutlet="
              salesChannelPills;
              context: { $implicit: item.salesChannels }
            "
          />
          } @case ("validity") { @if (item.validFor) {
          <div class="flex max-w-24 flex-col text-xs">
            <span>From {{ item.validFor.start ?? "—" }}</span>
            <span class="text-gray-500">To {{ item.validFor.end ?? "—" }}</span>
          </div>
          } @else {
          <span class="cell-empty">—</span>
          } } @case ("updated") {
          <!-- The price's own last update: when on top, who below; the exact
               time and full user live in the tooltip. -->
          @if (item.lastUpdate.missing) {
          <span class="cell-empty">—</span>
          } @else {
          <div
            class="last-update max-w-18"
            [attr.data-testid]="'enrichment-list-updated-' + item.key"
            [enrTooltip]="item.lastUpdate.tooltip"
            enrTooltipPosition="bottom"
          >
            <span class="last-update__when">
              @if (item.lastUpdate.recent) {
              <span
                class="last-update__dot"
                aria-hidden="true"
                [attr.data-testid]="'enrichment-list-recent-' + item.key"
              ></span>
              <span class="sr-only">Updated in the last 15 minutes.</span>
              } {{ item.lastUpdate.when }}
            </span>
            <span class="last-update__who">{{ item.lastUpdate.who }}</span>
          </div>
          } } }
        </ng-template>
      </enr-table>
    </div>
  </div>
</div>

<!-- Quick Preview — a right DRAWER (OmniarkDrawerService.showDrawerRef), not a
     modal: the list stays visible beside it so "is this the one?" can be
     answered by comparison. The drawer's own header carries the name, the
     id · product line, and the close button; this template owns only the
     status + primary action row and the body. -->
<ng-template #previewDrawerTemplate>
  @if (previewItem) {
  <div
    class="preview-card-content os-overlay"
    data-testid="enrichment-preview-drawer"
  >
    <header class="preview-head">
      <span [class]="'status-pill ' + statusClass(previewItem)">
        <span class="status-pill__dot"></span>
        {{ statusLabel(previewItem) }}
      </span>
      <span class="preview-head__shelf" [title]="shelfNames(previewItem)">
        {{ shelfNames(previewItem) }}
      </span>
      <div class="preview-head__actions">
        <enr-button
          variant="primary"
          size="small"
          data-testid="enrichment-preview-btn-edit"
          (clicked)="handleEditAction(previewItem)"
        >
          Open in editor →
        </enr-button>
      </div>
    </header>
    <div class="preview-body">
      <div class="preview-meta">
        <dl>
          <dt>Price</dt>
          <dd>{{ previewItem.title }}</dd>
          <dt>Price ID</dt>
          <dd class="mono">{{ previewItem.priceId }}</dd>
          <dt>Price version</dt>
          <dd class="mono">{{ previewItem.priceVersion || "—" }}</dd>
          @if (previewItem.productCode) {
          <dt>Product code</dt>
          <dd class="mono">{{ previewItem.productCode }}</dd>
          }
          <dt>Amount</dt>
          <dd>{{ previewItem.amount }}</dd>
          <dt>Status</dt>
          <dd>
            <enr-status
              [status]="previewItem.statusType"
              [customText]="statusLabel(previewItem)"
            />
          </dd>
          <dt>Shelves</dt>
          <dd>{{ shelfNames(previewItem) }}</dd>
          <dt>Last edit</dt>
          <dd>
            @if (previewItem.lastUpdate.missing) { — } @else {
            <span
              [enrTooltip]="previewItem.lastUpdate.tooltip"
              enrTooltipPosition="bottom"
              >{{ previewItem.lastUpdate.when }} · {{ previewItem.lastUpdate.who
              }}</span
            >
            }
          </dd>
          <dt>Enriched channels</dt>
          <dd>
            <ng-container
              *ngTemplateOutlet="
                enrichedChannelPills;
                context: { $implicit: previewItem.enrichedChannels }
              "
            />
          </dd>
          <dt>Sales channels</dt>
          <dd>
            @if (previewItem.salesChannels.length) { {{
            previewItem.salesChannels.join(", ") }} } @else { — }
          </dd>
          <dt>Product offering</dt>
          <dd>{{ previewItem.offeringName || "—" }}</dd>
          <dt>Product offering ID</dt>
          <dd class="mono">{{ previewItem.offeringId }}</dd>
          <dt>Product offering version</dt>
          <dd class="mono">{{ previewItem.offeringVersion || "—" }}</dd>
          <dt>Product line</dt>
          <dd>{{ previewItem.raw.offering?.productLine || "—" }}</dd>
          <dt>Product type</dt>
          <dd>{{ previewItem.raw.offering?.productType || "—" }}</dd>
          <dt>Offer classification</dt>
          <dd>{{ previewOfferClassification }}</dd>
        </dl>
      </div>
      <!-- Phone beside the facts, scaled down (see .preview-mock): the
           frame's native 320px is sized for the editor's rail, and at that
           size it pushed every attribute below the fold of the drawer. -->
      <div class="preview-mock">
        @if (previewLoading) {
        <div class="preview-status">Loading preview…</div>
        } @else if (previewError) {
        <div class="preview-status preview-status--error">
          {{ previewError }}
        </div>
        } @else {
        <div class="preview-stage-wrap">
          <!-- Bounded to the column: a renderer wider than the phone (the
               Online Store card row) scrolls inside its own strip instead of
               spilling over the facts. -->
          <app-enrichment-mobile-preview
            class="block w-full min-w-0"
            [priceIndexOverride]="previewPriceIndex"
            (priceIndexChange)="onPreviewPriceIndexChange($event)"
          ></app-enrichment-mobile-preview>
        </div>
        }
        <div class="preview-mock__caption">
          Quick preview · open the editor for full details
        </div>
      </div>
    </div>
  </div>
  }
</ng-template>

<!-- Package Filter Drawer Template -->
<ng-template #filterDrawerTemplate>
  <div class="w-full h-full">
    <app-package-filter-drawer
      (filterApplied)="onFilterApplied($event)"
      (filterCleared)="onFilterCleared()"
    ></app-package-filter-drawer>
  </div>
</ng-template>

<!-- The channels carrying an authored overlay of this price, capped like the
     sale channels below. The table passes its row as `item` for the testid;
     the preview drawer passes none so the id stays unique on the page. -->
<ng-template #enrichedChannelPills let-channels let-item="item">
  <div class="channel-cell">
    @if (channels.length) { @for (ch of visiblePills(channels); track ch) {
    <span class="channel-cell-pill channel-cell-pill--enriched">{{ ch }}</span>
    } @if (hiddenPillCount(channels); as more) {
    <span
      class="channel-cell-pill channel-cell-pill--more"
      [enrTooltip]="channels.join(' · ')"
      enrTooltipPosition="bottom"
      [enrTooltipShowDelay]="0"
      >+{{ more }}</span
    >
    } } @else {
    <span
      class="channel-cell-pill channel-cell-pill--not-enriched"
      [attr.data-testid]="
        item ? 'enrichment-list-not-enriched-' + item.key : null
      "
      [enrTooltip]="
        isChannelSelected
          ? 'No enrichment on ' +
            selectedChannel +
            ' yet. This row shows the master catalog data.'
          : 'No channel has an enrichment of this price yet.'
      "
      enrTooltipPosition="bottom"
      >Not enriched</span
    >
    }
  </div>
</ng-template>

<!-- A price's sale channels, capped inline with the rest behind a "+N" pill
     whose tooltip lists them all. -->
<ng-template #salesChannelPills let-channels>
  @if (channels?.length) {
  <div class="channel-cell">
    @for (ch of visiblePills(channels); track ch) {
    <span class="channel-cell-pill">{{ ch }}</span>
    } @if (hiddenPillCount(channels); as more) {
    <span
      class="channel-cell-pill channel-cell-pill--more"
      [enrTooltip]="channels.join(' · ')"
      enrTooltipPosition="bottom"
      [enrTooltipShowDelay]="0"
      >+{{ more }}</span
    >
    }
  </div>
  } @else {
  <div class="channel-cell">
    <span class="channel-cell-pill channel-cell-pill--none">None</span>
  </div>
  }
</ng-template>

<!-- Channel gate — only opens for the ApplicationOwner case (no nav-header
     channel scope) when the in-page pill is empty. Picking a channel here
     populates the store, drives x-channel for the edit GET / save PATCH,
     and navigates to the edit page. -->
<ng-template #channelGateModalTemplate>
  <app-enrichment-channel-gate
    [selected]="selectedChannel"
    [salesChannels]="pendingEditItem?.salesChannels ?? []"
    [allowedKeys]="pendingEditAllowedKeys"
    [loading]="pendingEditChannelsLoading"
    (pick)="selectChannelForEdit($event)"
    (close)="closeChannelGate()"
  />
</ng-template>

<!-- Bulk "Assign shelves" drawer — hosts the self-contained catalog picker
     that binds shelves onto the selected prices without opening the editor. -->
<ng-template #categoryAssignDrawerTemplate>
  @if (assignScope; as scope) {
  <app-enrichment-category-assign
    [rows]="selectedRows"
    [scope]="scope"
    (applied)="onCategoryAssignApplied($event)"
    (cancelled)="closeCategoryAssign()"
  />
  }
</ng-template>

<!-- The confirm step for every lifecycle action, bulk or row. -->
<ng-template #transitionDrawerTemplate>
  @if (transitionAction) {
  <app-enrichment-transition-drawer
    [action]="transitionAction"
    [rows]="transitionRows"
    [channel]="transitionChannel"
    [channelLabel]="currentChannelLabel"
    [sending]="transitionSending"
    [outcomes]="transitionOutcomes"
    (confirmed)="onTransitionConfirmed($event)"
    (refreshed)="onTransitionRowsRefreshed($event)"
    (closed)="closeTransition()"
  />
  }
</ng-template>

<!-- Pre-import confirmation: lets the user choose dry run (validate only,
     writes nothing) vs. a real import before the file is actually uploaded. -->
<ng-template #importOptionsModalTemplate>
  <div class="import-options-modal os-overlay">
    <p class="import-options-modal__file">{{ pendingImportFile?.name }}</p>
    <!-- The label WRAPS its control, so clicking the text toggles the box —
         <enr-checkbox> is an emulated-encapsulation component, so its inner
         <input type="checkbox"> is a real descendant and native label
         activation works. The rule only recognises native controls and can't
         see through the custom element; same disable as the zone/roaming
         forms use for their omniark checkboxes. -->
    <!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -->
    <label class="import-options-modal__dry-run">
      <enr-checkbox
        name="enrichmentImportDryRun"
        [checked]="importDryRun"
        (change)="onImportDryRunChange($event)"
      />
      <span> Dry run — validate only, no changes are written </span>
    </label>
    <div class="import-options-modal__actions">
      <enr-button
        variant="outline"
        size="small"
        (clicked)="cancelImportOptions()"
      >
        Cancel
      </enr-button>
      <enr-button
        variant="primary"
        size="small"
        (clicked)="confirmImportOptions()"
      >
        {{ importDryRun ? "Validate" : "Import" }}
      </enr-button>
    </div>
  </div>
</ng-template>

<!-- Import report — reuses the shared validation-errors modal (same
     "<sheet> · Row N" pattern as the easy-app package import). -->
<ng-template #importReportModalTemplate>
  <div class="import-report-modal os-overlay">
    <p class="import-report-modal__summary">{{ importReportSummary }}</p>
    @if (importReportTruncated) {
    <p class="import-report-modal__truncated">
      Showing the first errors only — some additional violations were not
      included in this report.
    </p>
    } @if (importValidationErrors().length > 0) {
    <app-import-validation-errors-modal
      [errors]="importValidationErrors()"
      [itemNoun]="importReportItemNoun"
      (closed)="closeImportReport()"
    />
    } @else {
    <div class="import-report-modal__actions">
      <enr-button
        variant="primary"
        size="small"
        (clicked)="closeImportReport()"
      >
        Close
      </enr-button>
    </div>
    }
  </div>
</ng-template>

<ng-template #packageExportOptionsModalTemplate>
  <div class="import-options-modal os-overlay">
    <!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -->
    <label class="import-options-modal__dry-run">
      <enr-checkbox
        name="packageExportLegacyLayout"
        [checked]="packageExportLegacyLayout"
        (change)="onPackageExportLegacyChange($event)"
      />
      <span> Legacy layout (package editor columns) </span>
    </label>
    <div class="import-options-modal__actions">
      <enr-button
        variant="outline"
        size="small"
        (clicked)="cancelPackageExportOptions()"
      >
        Cancel
      </enr-button>
      <enr-button
        data-testid="package-export-confirm"
        variant="primary"
        size="small"
        (clicked)="confirmPackageExportOptions()"
      >
        Export
      </enr-button>
    </div>
  </div>
</ng-template>
